/**
 * NON Theme DataLayer Bridge
 * Version: 1.0.0
 *
 * This script runs in the main browser context (not the pixel sandbox)
 * and handles:
 * 1. dataLayer initialisation for GTM
 * 2. Custom event publishing to Shopify Customer Events
 * 3. UTM parameter persistence
 * 4. First-party ID persistence
 * 5. Consent state management
 *
 * Included in theme.liquid via script tag
 */

(function () {
  'use strict';

  // Fix 2: Assign window.NON immediately so it's available even if later code errors
  window.NON = window.NON || {};

  const DEBUG = false;
  const VERSION = '1.0.0';

  function log(...args) {
    if (DEBUG) console.log('[NON Bridge]', ...args);
  }

  // ============================================
  // 1. dataLayer INITIALISATION
  // ============================================

  window.dataLayer = window.dataLayer || [];

  // Listen for pixel events from sandboxed iframe via postMessage
  window.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'non_pixel_event') {
      const payload = event.data.payload;
      if (!payload) {
        log('Received postMessage with empty payload');
        return;
      }
      applyPayloadOverrides(payload);
      window.dataLayer.push(payload);
      log('Received pixel event via postMessage:', payload.event, 'landing_page:', payload.attribution?.landing_page);
    }
  });

  function applyPayloadOverrides(payload) {
    if (!payload) return;
    const realPath = window.location.pathname;
    const realPathWithSearch = realPath + window.location.search;

    // Fix 3: Override page_path with real storefront path
    if (typeof payload.page_path !== 'undefined') {
      payload.page_path = realPath;
    }
    // Fix 3b: Override attribution.landing_page with real storefront path
    // The pixel runs in a sandbox where window.location is the sandbox URL,
    // so we must override with the parent's real path.
    if (!payload.attribution) {
      payload.attribution = {};
    }
    // Always replace sandbox paths with real path, regardless of current value
    const currentLanding = payload.attribution.landing_page || '';
    if (currentLanding.startsWith('SANDBOX_PATH:') || currentLanding.includes('/web-pixels@')) {
      payload.attribution.landing_page = realPathWithSearch;
    } else if (!currentLanding || currentLanding === '/') {
      payload.attribution.landing_page = realPathWithSearch;
    }
    // Also fix any nested landing_page references
    if (payload.landing_page) {
      const nestedLanding = payload.landing_page;
      if (nestedLanding.startsWith('SANDBOX_PATH:') || nestedLanding.includes('/web-pixels@')) {
        payload.landing_page = realPathWithSearch;
      }
    }
    // Fix 5: Inject current consent state
    payload.consent = getConsentState();
  }

  // Cart snapshot cache for synchronous reads
  let __nonCartSnapshot = window.theme?.cart || null;
  let __nonCartSnapshotPromise = null;

  function refreshCartSnapshot() {
    __nonCartSnapshotPromise = fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        __nonCartSnapshot = cart;
        __nonCartSnapshotPromise = null;
        log('Cart snapshot refreshed:', cart.item_count, 'items');
        return cart;
      })
      .catch(err => {
        __nonCartSnapshotPromise = null;
        log('Failed to refresh cart snapshot', err);
        throw err;
      });
    return __nonCartSnapshotPromise;
  }

  async function getCartSnapshot() {
    if (__nonCartSnapshotPromise) {
      return __nonCartSnapshotPromise;
    }
    if (__nonCartSnapshot) {
      return __nonCartSnapshot;
    }
    return refreshCartSnapshot();
  }

  function buildContentId(item, market) {
    // AU market: prefer SKU, fall back to legacy numeric variant ID
    if (market === 'AU' || !market) {
      if (item.sku) return item.sku;
      return String(item.variant_id || item.id || '');
    }
    // US market: BlendAI format blend_US_<productId>_<variantId>
    if (market === 'US') {
      const productId = String(item.product_id || '');
      const variantId = String(item.variant_id || item.id || '');
      if (productId && variantId) {
        return 'blend_US_' + productId + '_' + variantId;
      }
      return String(item.variant_id || item.id || '');
    }
    return String(item.variant_id || item.id || '');
  }

  function buildEcommerceFromSnapshot(cart, market) {
    // Prefer live snapshot, fall back to theme cart from liquid
    const liveCart = cart || __nonCartSnapshot || window.theme?.cart || null;
    if (!liveCart || !liveCart.items) {
      return { currency: 'AUD', value: 0, items: [] };
    }
    const currentMarket = market || window.__NON_MARKET || 'AU';
    const items = liveCart.items.map(item => ({
      item_id: buildContentId(item, currentMarket),
      item_name: item.product_title || item.title,
      item_variant: item.variant_title || 'Default Title',
      item_brand: item.vendor || 'NON',
      price: (item.price || 0) / 100,
      quantity: item.quantity,
      currency: liveCart.currency || 'AUD',
    }));
    const value = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    return {
      currency: liveCart.currency || 'AUD',
      value: value,
      items: items,
    };
  }
  // Fix 1: Synchronous click handler for checkout buttons — reads from snapshot (awaits in-flight refresh)
  document.addEventListener('click', async (event) => {
    const target = event.target.closest('a[href*="/checkout"], button[name="checkout"], [data-cart-checkout-button], .cart__checkout, .checkout-button, [data-action="checkout"], .payment-button-wrapper button');
    if (target) {
      log('Checkout button clicked, awaiting cart snapshot');
      const cart = await getCartSnapshot();
      const ecommerce = buildEcommerceFromSnapshot(cart);
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: ecommerce,
        consent: getConsentState(),
      });
      log('begin_checkout pushed with', ecommerce.items.length, 'items');
    }
  });

  // Fix 4: Listen for cart drawer open, push view_cart from fresh snapshot
  document.addEventListener('theme:cart-drawer:open', async () => {
    log('Cart drawer opened, fetching fresh cart snapshot');
    const cart = await refreshCartSnapshot();
    const ecommerce = buildEcommerceFromSnapshot(cart);
    window.dataLayer.push({
      event: 'view_cart',
      ecommerce: ecommerce,
      consent: getConsentState(),
    });
    log('view_cart pushed with', ecommerce.items.length, 'items');
  });

  // Track successful Baseline cart decreases, including a full line removal.
  // The cart snapshot is intentionally read before it is refreshed so the
  // removed item and its previous quantity are still available.
  document.body.addEventListener('baseline:cart:cartqtychange', async (event) => {
    const detail = event.detail || {};
    const previousQuantity = Number(detail.previousQuantity || 0);
    const nextQuantity = Number(detail.quantity || 0);
    const removedQuantity = previousQuantity - nextQuantity;

    if (removedQuantity <= 0) {
      refreshCartSnapshot();
      return;
    }

    const initialCart = await getCartSnapshot();
    const removedItem = initialCart?.items?.find(item =>
      String(item.key) === String(detail.key) ||
      String(item.variant_id) === String(detail.variantId)
    );

    if (removedItem) {
      const currency = initialCart.currency || 'AUD';
      const unitPrice = Number(removedItem.price || 0) / 100;
      const ecommercePayload = {
        currency: currency,
        value: unitPrice * removedQuantity,
        items: [{
          item_id: buildContentId(removedItem, window.__NON_MARKET || 'AU'),
          item_name: removedItem.product_title || removedItem.title,
          item_variant: removedItem.variant_title || 'Default Title',
          item_brand: removedItem.vendor || 'NON',
          price: unitPrice,
          quantity: removedQuantity,
          currency: currency,
        }],
      };

      window.NON.publishEvent('remove_from_cart', {
        ecommerce: ecommercePayload,
        page_type: 'cart',
      });
      log('remove_from_cart published for:', removedItem.product_title || removedItem.title, removedQuantity);
    } else {
      log('Could not find removed item in the pre-change cart snapshot.', detail.key);
    }

    refreshCartSnapshot();
  });

  // New event: promo_popup_submitted
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[action="/contact"]');
    const isPromoModalForm = form && form.closest('[data-modal-id="promo-popup"], [x-ref="promo-modal"]');

    if (isPromoModalForm) {
      const emailInput = form.querySelector('input[type="email"]');
      const email = emailInput ? emailInput.value : 'unknown';

      window.NON.publishEvent('promo_popup_submitted', {
        email: email,
        source: 'promo_popup',
        page_type: 'newsletter',
      });
      log('promo_popup_submitted event published for email:', email);
    }
  });

  // New events: engagement_time_30s, engagement_time_60s, engagement_time_120s
  let engagementTimers = {};
  let engagedTime = 0;
  const engagementInterval = 1000; // Check every second

  const engagementThresholds = {
    'engagement_time_30s': 30 * 1000,
    'engagement_time_60s': 60 * 1000,
    'engagement_time_120s': 120 * 1000,
  };

  function trackEngagement() {
    engagedTime += engagementInterval;

    for (const eventName in engagementThresholds) {
      if (engagedTime >= engagementThresholds[eventName] && !engagementTimers[eventName]) {
        engagementTimers[eventName] = true; // Mark as fired
        window.NON.publishEvent(eventName, {
          engagement_time_seconds: engagedTime / 1000,
          page_type: window.__NON_PAGE_TYPE,
        });
        log(eventName, 'fired at', engagedTime / 1000, 'seconds');
      }
    }
  }

  let engagementIntervalId = null;
  let lastActivity = Date.now();
  const activityTimeout = 5 * 1000; // 5 seconds of inactivity to pause engagement tracking

  function startEngagementTracking() {
    if (engagementIntervalId === null) {
      engagementIntervalId = setInterval(() => {
        if (Date.now() - lastActivity < activityTimeout) {
          trackEngagement();
        } else {
          // Pause tracking during inactivity, but don't reset engagedTime
        }
      }, engagementInterval);
      log('Engagement tracking started');
    }
  }

  function resetActivityTimer() {
    lastActivity = Date.now();
    startEngagementTracking(); // Ensure tracking is active if user returns after inactivity
  }

  // Listen for user activity to keep engagement tracking active
  ['mousemove', 'keydown', 'scroll', 'click'].forEach(eventType => {
    document.addEventListener(eventType, resetActivityTimer, { passive: true });
  });

  // Start tracking initially
  startEngagementTracking();

  // New event: scroll_75
  let scroll75Fired = false;

  function trackScrollDepth() {
    if (scroll75Fired) return;

    const documentHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY || window.pageYOffset;

    const scrollPercent = (scrollY + viewportHeight) / documentHeight;

    if (scrollPercent >= 0.75) {
      scroll75Fired = true;
      window.NON.publishEvent('scroll_75', {
        scroll_depth_percent: 75,
        page_type: window.__NON_PAGE_TYPE,
      });
      log('scroll_75 event fired');
      document.removeEventListener('scroll', debouncedScrollHandler);
    }
  }

  let scrollTimeout = null;
  function debouncedScrollHandler() {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    scrollTimeout = setTimeout(trackScrollDepth, 100); // Debounce by 100ms
  }

  document.addEventListener('scroll', debouncedScrollHandler, { passive: true });
  // Initial check in case page loads already scrolled
  document.addEventListener('DOMContentLoaded', trackScrollDepth);

  // New events: video_started, video_completed
  const trackedVideos = new WeakMap(); // To track if video_started has fired for a video

  function setupVideoTracking() {
    document.querySelectorAll('video').forEach(video => {
      if (!video.dataset.nonVideoTracked) {
        video.dataset.nonVideoTracked = 'true'; // Mark as tracked
        video.addEventListener('play', () => {
          if (!trackedVideos.has(video)) {
            window.NON.publishEvent('video_started', {
              video_title: video.title || video.dataset.title || 'Unnamed Video',
              video_src: video.currentSrc || video.src || '',
              page_type: window.__NON_PAGE_TYPE,
            });
            log('video_started event fired for:', video.title || video.dataset.title || 'Unnamed Video');
            trackedVideos.set(video, true);
          }
        }, { once: true }); // Only fire video_started once per play event, use WeakMap for overall state

        video.addEventListener('ended', () => {
          window.NON.publishEvent('video_completed', {
            video_title: video.title || video.dataset.title || 'Unnamed Video',
            video_src: video.currentSrc || video.src || '',
            page_type: window.__NON_PAGE_TYPE,
          });
          log('video_completed event fired for:', video.title || video.dataset.title || 'Unnamed Video');
          trackedVideos.delete(video); // Reset for next play if needed
        });

        // Reset on pause if we want to track multiple plays, but the prompt says 'started' not 'resumed'
        // For now, `once: true` on play for video_started is correct.
      }
    });
  }

  // Setup tracking for existing videos and any new ones dynamically loaded
  document.addEventListener('DOMContentLoaded', setupVideoTracking);
  // Potentially re-run setupVideoTracking on DOM changes if videos are loaded dynamically
  // E.g., via a MutationObserver, but for now, DOMContentLoaded is sufficient given standard theme structure.




  // Also refresh snapshot whenever cart is updated
  document.addEventListener('theme:cart:update', () => {
    log('theme:cart:update fired, refreshing snapshot');
    refreshCartSnapshot();
  });

  // Fallback for older cart event names
  document.addEventListener('cart:updated', () => {
    log('cart:updated fired, refreshing snapshot');
    refreshCartSnapshot();
  });

  document.body.addEventListener('baseline:cart:update', () => {
    log('baseline:cart:update fired, refreshing snapshot');
    refreshCartSnapshot();
  });

  // Warm snapshot on page load if cart exists
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshCartSnapshot);
  } else {
    refreshCartSnapshot();
  }

  // Push initial page context before GTM loads
  window.dataLayer.push({
    event: 'gtm.js',
    ecommerce: null,
    page: {
      type: window.__NON_PAGE_TYPE || 'unknown',
      path: window.location.pathname,
      url: window.location.href,
      title: document.title,
    },
    non: {
      version: VERSION,
      theme: window.THEMENAME || 'Baseline',
      theme_version: window.THEMEVERSION || '',
    },
  });

  log('dataLayer initialised');

  // ============================================
  // 2. UTM PERSISTENCE
  // ============================================

  const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const UTM_STORAGE_KEY = 'non_utm_attribution';
  const UTM_TTL_DAYS = 30;

  function getUTMsFromURL() {
    const params = new URLSearchParams(window.location.search);
    const utms = {};
    let hasAny = false;

    for (const key of UTM_PARAMS) {
      const value = params.get(key);
      if (value) {
        utms[key] = value;
        hasAny = true;
      }
    }

    return hasAny ? utms : null;
  }

  function getStoredUTMs() {
    try {
      const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      const age = Date.now() - (parsed.timestamp || 0);
      const maxAge = UTM_TTL_DAYS * 24 * 60 * 60 * 1000;

      if (age > maxAge) {
        sessionStorage.removeItem(UTM_STORAGE_KEY);
        return null;
      }

      return parsed.utms || null;
    } catch (e) {
      return null;
    }
  }

  function storeUTMs(utms) {
    try {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({
        utms,
        timestamp: Date.now(),
      }));
    } catch (e) {
      log('Failed to store UTMs', e);
    }
  }

  function initUTMPersistence() {
    const urlUTMs = getUTMsFromURL();

    if (urlUTMs) {
      storeUTMs(urlUTMs);
      log('UTMs captured from URL:', urlUTMs);
    }

    const storedUTMs = getStoredUTMs();
    if (storedUTMs) {
      window.__NON_UTM_ATTRIBUTION = storedUTMs;
      log('UTMs loaded from storage:', storedUTMs);
    }
  }

  initUTMPersistence();

  // ============================================
  // 3. FIRST-PARTY ID PERSISTENCE
  // ============================================

  const FPID_KEY = 'non_fpid';

  function getOrCreateFPID() {
    try {
      let fpid = localStorage.getItem(FPID_KEY);
      if (!fpid) {
        fpid = 'non_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem(FPID_KEY, fpid);
      }
      return fpid;
    } catch (e) {
      // Fallback if localStorage is blocked
      return 'non_fallback_' + Date.now();
    }
  }

  window.__NON_FPID = getOrCreateFPID();
  log('FPID:', window.__NON_FPID);

  // ============================================
  // 4. CONSENT STATE MANAGEMENT
  // ============================================

  function getConsentState() {
    // Default: deny all until consent is given
    const defaultState = {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
    };

    // Check for Shopify Customer Privacy API
    if (window.Shopify && window.Shopify.customerPrivacy) {
      const privacy = window.Shopify.customerPrivacy;

      try {
        return {
          ad_storage: privacy.marketingAllowed() ? 'granted' : 'denied',
          ad_user_data: privacy.marketingAllowed() ? 'granted' : 'denied',
          ad_personalization: privacy.marketingAllowed() ? 'granted' : 'denied',
          analytics_storage: privacy.analyticsProcessingAllowed() ? 'granted' : 'denied',
          functionality_storage: privacy.preferencesProcessingAllowed() ? 'granted' : 'denied',
          personalization_storage: privacy.preferencesProcessingAllowed() ? 'granted' : 'denied',
          security_storage: 'granted',
          consent_id: privacy.getTrackingConsent?.() || '',
        };
      } catch (e) {
        log('Error reading Shopify consent', e);
      }
    }

    // Check for existing consent cookie (common CMP pattern)
    const consentCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('consent='));

    if (consentCookie) {
      try {
        const consent = JSON.parse(decodeURIComponent(consentCookie.split('=')[1]));
        return {
          ad_storage: consent.ad_storage || 'denied',
          ad_user_data: consent.ad_user_data || 'denied',
          ad_personalization: consent.ad_personalization || 'denied',
          analytics_storage: consent.analytics_storage || 'denied',
          functionality_storage: consent.functionality_storage || 'denied',
          personalization_storage: consent.personalization_storage || 'denied',
          security_storage: 'granted',
        };
      } catch (e) {
        log('Error parsing consent cookie', e);
      }
    }

    return defaultState;
  }

  function pushConsentUpdate() {
    const consent = getConsentState();
    window.dataLayer.push({
      event: 'consent_update',
      consent: consent,
    });
    log('Consent pushed:', consent);
  }

  // Listen for consent changes
  document.addEventListener('visitorConsentCollected', pushConsentUpdate);
  document.addEventListener('cookie_consent_update', pushConsentUpdate);

  // Initial consent push
  pushConsentUpdate();

  // ============================================
  // 5. KLAVIYO EVENT BRIDGE
  // ============================================

  // The official Shopify app embed owns the onsite library. Queue custom
  // events here without injecting a second copy of Klaviyo.

  // Helper to push Klaviyo events from dataLayer events
  window.NON = window.NON || {};
  window.NON.trackKlaviyoEvent = function(eventName, properties) {
    var _learnq = window._learnq = window._learnq || [];
    _learnq.push(['track', eventName, properties]);
    log('Klaviyo track:', eventName, properties);
  };

  // ============================================
  // 6. CUSTOM EVENT PUBLISHING HELPERS
  // ============================================

  /**
   * Publish a custom event to Shopify Customer Events
   * This allows the Custom Web Pixel to capture it
   */

  window.NON.publishEvent = function (eventName, data) {
    if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
      window.Shopify.analytics.publish('non:' + eventName, {
        event_name: eventName,
        ...data,
        timestamp: new Date().toISOString(),
        fpid: window.__NON_FPID,
        utm: window.__NON_UTM_ATTRIBUTION || {},
      });
      log('Published event:', eventName, data);
    } else {
      log('Shopify.analytics.publish not available, pushing directly to dataLayer');
      window.dataLayer.push({
        event: eventName,
        ...data,
        timestamp: new Date().toISOString(),
        fpid: window.__NON_FPID,
        utm: window.__NON_UTM_ATTRIBUTION || {},
      });
    }
  };

  // Pre-built event helpers for common NON interactions

  window.NON.trackQuizStart = function (quizName) {
    window.NON.publishEvent('quiz_started', {
      quiz_name: quizName,
      page_type: 'quiz',
    });
  };

  window.NON.trackQuizComplete = function (quizName, result, recommendations) {
    window.NON.publishEvent('quiz_completed', {
      quiz_name: quizName,
      result: result,
      recommendations: recommendations || [],
      page_type: 'quiz',
    });
  };

  window.NON.trackSommelierStart = function () {
    window.NON.publishEvent('sommelier_started', {
      page_type: 'sommelier',
    });
  };

  // non-somm.js calls the guarded generic form NON.started('somm', {...}).
  // Alias it onto publishEvent so the somm question event is not lost.
  window.NON.started = function (name, data) {
    window.NON.publishEvent((name || 'event') + '_started', Object.assign({ page_type: name || 'event' }, data || {}));
  };

  window.NON.trackSommelierComplete = function (recommendation) {
    window.NON.publishEvent('sommelier_completed', {
      recommendation: recommendation,
      page_type: 'sommelier',
    });
  };

  window.NON.trackSignup = function (source, email) {
    window.NON.publishEvent('sign_up', {
      method: source,
      email: email,
      page_type: 'account',
    });
  };

  window.NON.trackLead = function (source, details) {
    window.NON.publishEvent('generate_lead', {
      source: source,
      ...details,
      page_type: 'contact',
    });
  };

  window.NON.trackAddToWishlist = function (product) {
    window.NON.publishEvent('add_to_wishlist', {
      ecommerce: {
        currency: product.currency || 'AUD',
        value: product.price || 0,
        items: [{
          item_id: product.sku || String(product.id || ''),
          item_name: product.title,
          item_variant: product.variant || 'Default',
          price: product.price || 0,
          quantity: 1,
        }],
      },
      page_type: 'product',
    });
  };

  window.NON.trackNewsletterSignup = function (email, source) {
    window.NON.publishEvent('newsletter_signup', {
      email: email,
      source: source || 'footer',
      page_type: 'newsletter',
    });
  };

  // ============================================
  // 7. LISTEN FOR PIXEL-QUEUED EVENTS
  // ============================================

  // The Custom Web Pixel may queue events if parent.dataLayer isn't ready
  // This drains the queue and applies the same overrides as postMessage events
  window.addEventListener('load', function () {
    if (window.__nonDataLayerQueue && window.__nonDataLayerQueue.length) {
      log('Draining pixel event queue:', window.__nonDataLayerQueue.length);
      while (window.__nonDataLayerQueue.length) {
        const event = window.__nonDataLayerQueue.shift();
        applyPayloadOverrides(event);
        window.dataLayer.push(event);
      }
    }
  });

  // ============================================
  // 8. PAGE TYPE DETECTION
  // ============================================

  function detectPageType() {
    const path = window.location.pathname;
    const template = document.body.dataset.template || '';

    if (path === '/' || path === '') return 'home';
    if (path.includes('/products/')) return 'product';
    if (path.includes('/collections/')) return 'collection';
    if (path.includes('/cart')) return 'cart';
    if (path.includes('/checkout')) return 'checkout';
    if (path.includes('/blogs/')) return 'article';
    if (path.includes('/pages/')) return 'page';
    if (path.includes('/search')) return 'search';
    if (path.includes('/account')) return 'account';
    if (template.includes('product')) return 'product';
    if (template.includes('collection')) return 'collection';
    if (template.includes('cart')) return 'cart';

    return 'page';
  }

  window.__NON_PAGE_TYPE = detectPageType();
  log('Page type detected:', window.__NON_PAGE_TYPE);

  log('NON Bridge v' + VERSION + ' initialised');
})();
